export type PartyPetAttachmentSlot = "head" | "eyes" | "face" | "neck" | "back" | "feet";

export type NormalizedBounds = { x: number; y: number; width: number; height: number };
export type PartyPetAttachmentAnchor = { x: number; y: number; scale: number; rotation: number };
export type PartyPetAttachmentOverride = Partial<PartyPetAttachmentAnchor>;

type AccessoryGeometry = {
  slot: PartyPetAttachmentSlot;
  bounds: NormalizedBounds;
  origin: { x: number; y: number };
  scale: number;
  offsetX?: number;
  offsetY?: number;
  rotation?: number;
};

export type ResolvedPartyPetAttachment = PartyPetAttachmentAnchor & {
  slot: PartyPetAttachmentSlot;
  leftPct: number;
  topPct: number;
  widthPct: number;
  shiftXPct: number;
  shiftYPct: number;
  originXPct: number;
  originYPct: number;
  zIndex: number;
};

// Alpha-derived optical bounds. Attachments are measured against the visible pet,
// not the transparent square around it. This makes the system resolution-independent.
export const PARTY_PET_VISIBLE_BOUNDS: Record<string, NormalizedBounds> = {
  alien: { x: .0703, y: .0735, width: .6390, height: .8754 },
  blob: { x: .1115, y: .3248, width: .8726, height: .6592 },
  cake: { x: .1118, y: .0159, width: .6709, height: .8790 },
  cloud: { x: .0160, y: .1178, width: .8307, height: .7452 },
  fluffball: { x: .1146, y: .2045, width: .8567, height: .7796 },
  ghost: { x: .0160, y: .3930, width: .7859, height: .5911 },
  mushroom: { x: .0159, y: .0863, width: .7675, height: .8626 },
  octopop: { x: .0799, y: .1885, width: .8019, height: .7955 },
  party_bird: { x: .0159, y: .0446, width: .8089, height: .9395 },
  raccoon: { x: .0159, y: .0350, width: .8185, height: .8599 },
  robot: { x: .1438, y: .2524, width: .7572, height: .6965 },
  sprout_frog: { x: .1502, y: .1019, width: .6805, height: .8822 },
  star_cat: { x: .1210, y: .0159, width: .7611, height: .8854 },
  tiny_dino: { x: .0288, y: .1561, width: .7029, height: .8280 },
  tiny_dragon: { x: .0605, y: .0639, width: .9236, height: .8914 },
  turtle: { x: .0159, y: .2364, width: .8439, height: .7476 },
};

const anchors = (head: PartyPetAttachmentAnchor, eyes: PartyPetAttachmentAnchor, face: PartyPetAttachmentAnchor, neck: PartyPetAttachmentAnchor, back: PartyPetAttachmentAnchor, feet: PartyPetAttachmentAnchor) => ({ head, eyes, face, neck, back, feet });
const a = (x: number, y: number, scale: number, rotation = 0): PartyPetAttachmentAnchor => ({ x, y, scale, rotation });

// Every creature owns six semantic landmarks. Values are normalized inside its
// visible alpha bounds, so the same data works for thumbnails, heroes, TV pets,
// animations, and exported portraits.
export const PARTY_PET_ATTACHMENT_ANCHORS: Record<string, Record<PartyPetAttachmentSlot, PartyPetAttachmentAnchor>> = {
  alien: anchors(a(.50, .08, .68), a(.50, .47, .68), a(.50, .61, .43), a(.50, .70, .44), a(.51, .57, 1.02), a(.50, .94, .76)),
  blob: anchors(a(.50, .08, .65), a(.50, .40, .67), a(.50, .57, .42), a(.50, .72, .43), a(.50, .57, 1.02), a(.50, .93, .78)),
  cake: anchors(a(.50, .18, .64), a(.50, .53, .64), a(.50, .65, .40), a(.50, .73, .42), a(.50, .58, .98), a(.50, .91, .75)),
  cloud: anchors(a(.52, .10, .70), a(.52, .52, .66), a(.52, .66, .42), a(.52, .75, .43), a(.52, .59, 1.04), a(.52, .94, .76)),
  fluffball: anchors(a(.51, .08, .68), a(.51, .41, .65), a(.51, .56, .41), a(.51, .70, .43), a(.51, .58, 1.04), a(.51, .94, .76)),
  ghost: anchors(a(.50, .10, .66), a(.50, .42, .64), a(.50, .59, .40), a(.50, .72, .42), a(.50, .60, 1.03), a(.50, .92, .78)),
  mushroom: anchors(a(.50, .13, .78), a(.50, .57, .56), a(.50, .68, .37), a(.50, .76, .39), a(.50, .59, 1.02), a(.50, .93, .70)),
  octopop: anchors(a(.50, .09, .66), a(.50, .40, .64), a(.50, .56, .40), a(.50, .67, .42), a(.50, .55, 1.02), a(.50, .91, .84)),
  party_bird: anchors(a(.48, .18, .58, -2), a(.48, .51, .58), a(.48, .63, .36), a(.48, .72, .40), a(.50, .58, 1.05), a(.48, .94, .70)),
  raccoon: anchors(a(.42, .12, .61), a(.42, .43, .58), a(.42, .57, .37), a(.43, .66, .39), a(.55, .56, 1.00, 4), a(.43, .91, .72)),
  robot: anchors(a(.50, .16, .62), a(.50, .38, .61), a(.50, .52, .38), a(.50, .62, .42), a(.50, .55, 1.02), a(.50, .91, .72)),
  sprout_frog: anchors(a(.50, .30, .66), a(.50, .48, .64), a(.50, .61, .40), a(.50, .72, .43), a(.50, .59, 1.02), a(.50, .94, .76)),
  star_cat: anchors(a(.50, .16, .64), a(.50, .42, .61), a(.50, .56, .38), a(.50, .65, .40), a(.50, .57, 1.02), a(.51, .91, .72)),
  tiny_dino: anchors(a(.49, .14, .65), a(.49, .38, .61), a(.49, .53, .39), a(.50, .65, .42), a(.49, .57, 1.03), a(.51, .92, .73)),
  tiny_dragon: anchors(a(.53, .15, .60), a(.53, .40, .58), a(.53, .54, .37), a(.53, .66, .40), a(.50, .55, .98), a(.53, .92, .69)),
  turtle: anchors(a(.26, .22, .52, -5), a(.26, .43, .50, -4), a(.26, .59, .34, -4), a(.39, .67, .36, -3), a(.63, .47, .88, 3), a(.51, .91, .72)),
};

const ACCESSORY_GEOMETRY: Record<string, AccessoryGeometry> = {
  birthday_hat: { slot: "head", bounds: { x: .1943, y: .0860, width: .6561, height: .8917 }, origin: { x: .5, y: .91 }, scale: .82 },
  crown: { slot: "head", bounds: { x: .1310, y: .2548, width: .7348, height: .6210 }, origin: { x: .5, y: .91 }, scale: .62 },
  tiara: { slot: "head", bounds: { x: .0987, y: .2492, width: .8185, height: .5335 }, origin: { x: .5, y: .92 }, scale: .62 },
  cowboy_hat: { slot: "head", bounds: { x: .0447, y: .1949, width: .8978, height: .6805 }, origin: { x: .5, y: .88 }, scale: .68 },
  wizard_hat: { slot: "head", bounds: { x: .0351, y: .0511, width: .8626, height: .8850 }, origin: { x: .5, y: .91 }, scale: .78 },
  chef_hat: { slot: "head", bounds: { x: .1465, y: .2748, width: .7229, height: .5974 }, origin: { x: .5, y: .91 }, scale: .62 },
  pirate_hat: { slot: "head", bounds: { x: .0732, y: .1757, width: .9108, height: .7220 }, origin: { x: .5, y: .82 }, scale: .78 },
  tiny_top_hat: { slot: "head", bounds: { x: .0767, y: .1597, width: .8275, height: .6933 }, origin: { x: .5, y: .89 }, scale: .58 },
  sunglasses: { slot: "eyes", bounds: { x: .0319, y: .4363, width: .9361, height: .3917 }, origin: { x: .5, y: .52 }, scale: .82 },
  party_glasses: { slot: "eyes", bounds: { x: .0159, y: .2971, width: .8822, height: .4696 }, origin: { x: .5, y: .53 }, scale: .80 },
  mustache: { slot: "face", bounds: { x: .0796, y: .3248, width: .8662, height: .2834 }, origin: { x: .5, y: .5 }, scale: .78 },
  bow_tie: { slot: "neck", bounds: { x: .1178, y: .4045, width: .7166, height: .4140 }, origin: { x: .5, y: .50 }, scale: .68, offsetY: .08 },
  superhero_cape: { slot: "back", bounds: { x: .0160, y: .0799, width: .9265, height: .8147 }, origin: { x: .5, y: .18 }, scale: .78, offsetY: -.04, rotation: 3 },
  oversized_shoes: { slot: "feet", bounds: { x: .0447, y: .1561, width: .8882, height: .6401 }, origin: { x: .5, y: .98 }, scale: .62, offsetY: .15 },
};

// A small number of silhouettes need pair-specific corrections beyond their
// semantic slot. These are production defaults; the developer calibrator can
// still override any individual pair on top of them.
export const PARTY_PET_ATTACHMENT_PAIR_ADJUSTMENTS: Record<string, PartyPetAttachmentOverride> = {
  "ghost:sunglasses": { y: .20, scale: .42 },
  "ghost:party_glasses": { y: .20, scale: .41 },
  "robot:sunglasses": { y: .33, scale: .47 },
  "robot:party_glasses": { y: .33, scale: .46 },
  "ghost:mustache": { y: .28, scale: .27 },
  "turtle:mustache": { x: .18, y: .40, scale: .18, rotation: -4 },
  "robot:mustache": { y: .45, scale: .28 },
  "star_cat:mustache": { y: .44, scale: .27 },
  "party_bird:mustache": { y: .62, scale: .20, rotation: -2 },
  "ghost:bow_tie": { y: .50, scale: .28 },
  "turtle:bow_tie": { x: .28, y: .60, scale: .23, rotation: -4 },
  "party_bird:bow_tie": { y: .66, scale: .27, rotation: -2 },
  "turtle:oversized_shoes": { x: .28, y: 1.05, scale: .39, rotation: -4 },
  "blob:superhero_cape": { scale: .68 },
  "sprout_frog:superhero_cape": { scale: 1.05 },
  "cake:superhero_cape": { scale: 1.00 },
  "cloud:superhero_cape": { scale: .68 },
  "ghost:superhero_cape": { scale: .86 },
  "party_bird:crown": { x: .48, y: .34, scale: .34, rotation: -2 },
  "party_bird:tiara": { x: .48, y: .34, scale: .34, rotation: -2 },
  "party_bird:cowboy_hat": { x: .48, y: .34, scale: .36, rotation: -2 },
  "party_bird:wizard_hat": { x: .48, y: .34, scale: .40, rotation: -2 },
  "party_bird:chef_hat": { x: .48, y: .34, scale: .34, rotation: -2 },
  "party_bird:pirate_hat": { x: .48, y: .34, scale: .40, rotation: -2 },
  "party_bird:tiny_top_hat": { x: .48, y: .34, scale: .30, rotation: -2 },
};

export function partyPetAccessoryKey(item: string) {
  if (item === "party hat" || item === "birthday hat") return "birthday_hat";
  if (item.includes("crown")) return "crown";
  return item.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");
}

export function partyPetAccessorySlot(item: string): PartyPetAttachmentSlot {
  return ACCESSORY_GEOMETRY[partyPetAccessoryKey(item)]?.slot || "head";
}

export function partyPetAttachmentOverrideKey(creature: string, item: string) {
  return `${creature}:${partyPetAccessoryKey(item)}`;
}

export function resolvePartyPetAttachment(creature: string, item: string, override: PartyPetAttachmentOverride = {}): ResolvedPartyPetAttachment {
  const key = partyPetAccessoryKey(item);
  const accessory = ACCESSORY_GEOMETRY[key] || ACCESSORY_GEOMETRY.birthday_hat;
  const petBounds = PARTY_PET_VISIBLE_BOUNDS[creature] || { x: 0, y: 0, width: 1, height: 1 };
  const petAnchors = PARTY_PET_ATTACHMENT_ANCHORS[creature] || PARTY_PET_ATTACHMENT_ANCHORS.blob;
  const base = petAnchors[accessory.slot];
  const tuned = PARTY_PET_ATTACHMENT_PAIR_ADJUSTMENTS[`${creature}:${key}`] || {};
  const finalAnchor: PartyPetAttachmentAnchor = {
    x: override.x ?? tuned.x ?? base.x + (accessory.offsetX || 0),
    y: override.y ?? tuned.y ?? base.y + (accessory.offsetY || 0),
    scale: override.scale ?? tuned.scale ?? base.scale * accessory.scale,
    rotation: override.rotation ?? tuned.rotation ?? base.rotation + (accessory.rotation || 0),
  };
  const originX = accessory.bounds.x + accessory.origin.x * accessory.bounds.width;
  const originY = accessory.bounds.y + accessory.origin.y * accessory.bounds.height;
  return {
    ...finalAnchor,
    slot: accessory.slot,
    leftPct: (petBounds.x + finalAnchor.x * petBounds.width) * 100,
    topPct: (petBounds.y + finalAnchor.y * petBounds.height) * 100,
    widthPct: finalAnchor.scale * petBounds.width / accessory.bounds.width * 100,
    shiftXPct: -originX * 100,
    shiftYPct: -originY * 100,
    originXPct: originX * 100,
    originYPct: originY * 100,
    zIndex: accessory.slot === "back" ? 1 : 4,
  };
}

export function partyPetAttachmentStyle(creature: string, item: string, override: PartyPetAttachmentOverride = {}) {
  const placement = resolvePartyPetAttachment(creature, item, override);
  return `--attachment-x:${placement.leftPct.toFixed(3)}%;--attachment-y:${placement.topPct.toFixed(3)}%;--attachment-width:${placement.widthPct.toFixed(3)}%;--attachment-shift-x:${placement.shiftXPct.toFixed(3)}%;--attachment-shift-y:${placement.shiftYPct.toFixed(3)}%;--attachment-origin-x:${placement.originXPct.toFixed(3)}%;--attachment-origin-y:${placement.originYPct.toFixed(3)}%;--attachment-rotation:${placement.rotation.toFixed(2)}deg;z-index:${placement.zIndex}`;
}
